/**
 * Thread execution state without a Thread Queue.
 *
 * A thread's work in progress is whatever its provider session says it is: the
 * Agent Provider owns follow-up ordering, so AFAR keeps no entries of its own
 * and derives busy, revert-readiness and execution phase from the session and
 * the latest turn alone.
 *
 * @module threadExecution
 */

/** What an execution-state decision is allowed to look at. */
export interface ThreadExecutionEvidence {
  readonly sessionStatus?: string | null | undefined;
  readonly latestTurnState?: string | null | undefined;
}

/**
 * A thread is idle only when it has no turn awaiting provider acknowledgement
 * and no running turn.
 *
 * This is the one place that sentence is code. Deriving it again from a
 * presentation-level phase is how a client and its Environment come to
 * disagree about the same thread.
 */
export function isThreadBusy(evidence: ThreadExecutionEvidence): boolean {
  return evidence.sessionStatus === "starting" || evidence.latestTurnState === "running";
}

/**
 * Checkpoint Revert requires an idle thread and a live provider session: a
 * rewind that raced a running turn would restore files underneath it and roll
 * back history the turn is still appending to.
 *
 * Whether the bound Agent Provider can rewind its own conversation at all is a
 * provider capability, not an execution rule, and is answered by
 * ProviderService.
 */
export function isThreadReadyForCheckpointRevert(evidence: ThreadExecutionEvidence): boolean {
  return evidence.sessionStatus === "ready" && !isThreadBusy(evidence);
}

export type ThreadExecutionPhase = "running" | "provider-running" | "idle";

/**
 * The thread's work in progress, including turns the provider began on its own.
 * Provider-initiated work is named apart from a dispatched turn so a surface
 * can say which one the user is looking at.
 */
export function deriveThreadExecutionPhase(
  turn?: { readonly origin: "dispatched" | "provider-initiated"; readonly state: string } | null,
): ThreadExecutionPhase {
  if (turn?.state !== "running") return "idle";
  return turn.origin === "provider-initiated" ? "provider-running" : "running";
}

export interface RevertibleThreadMessage {
  readonly id: string;
  readonly role: string;
  readonly turnId: string | null;
  readonly createdAt: string;
}

export interface RevertibleThreadActivity {
  readonly id?: string | undefined;
  readonly kind: string;
  readonly payload?: unknown;
  readonly turnId: string | null;
  readonly createdAt: string;
}

/**
 * Keep exactly the conversation prefix represented by a checkpoint revert.
 *
 * User messages are created before a provider turn exists, so their direct
 * `turnId` is commonly null; `messageTurnIds` supplies the binding the
 * projection recovered for them.
 */
export function retainThreadMessagesAfterRevert<T extends RevertibleThreadMessage>(input: {
  readonly messages: ReadonlyArray<T>;
  readonly retainedTurnIds: ReadonlySet<string>;
  readonly turnCount: number;
  readonly messageTurnIds?: ReadonlyMap<string, string> | undefined;
}): T[] {
  const effectiveTurnId = (message: T): string | null =>
    message.turnId ?? input.messageTurnIds?.get(message.id) ?? null;

  const retainedMessageIds = new Set<string>();
  for (const message of input.messages) {
    const turnId = effectiveTurnId(message);
    if (message.role === "system" || (turnId !== null && input.retainedTurnIds.has(turnId))) {
      retainedMessageIds.add(message.id);
    }
  }

  for (const role of ["user", "assistant"] as const) {
    const retainedCount = input.messages.filter(
      (message) => message.role === role && retainedMessageIds.has(message.id),
    ).length;
    const missingCount = Math.max(0, input.turnCount - retainedCount);
    if (missingCount === 0) continue;

    const fallbackMessages = input.messages
      .filter(
        (message) =>
          message.role === role &&
          !retainedMessageIds.has(message.id) &&
          effectiveTurnId(message) === null,
      )
      .toSorted(
        (left, right) =>
          left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
      )
      .slice(0, missingCount);
    for (const message of fallbackMessages) retainedMessageIds.add(message.id);
  }

  return input.messages.filter((message) => retainedMessageIds.has(message.id));
}

/** Drop every activity bound to a turn the revert discarded. */
export function retainThreadActivitiesAfterRevert<T extends RevertibleThreadActivity>(
  activities: ReadonlyArray<T>,
  retainedTurnIds: ReadonlySet<string>,
): T[] {
  return activities.filter(
    (activity) => activity.turnId === null || retainedTurnIds.has(activity.turnId),
  );
}
